import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiChevronRight, FiEdit2, FiExternalLink, FiFileText } from "react-icons/fi";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { PublicationChannel, SourcingEventStatus, SourcingEventV2Dto } from "../../../autogen/bff-api";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { Explanation } from "../../../common/support/Explanation/Explanation";
import { urls } from "../../../urls";
import {
  formatEstimatedValue,
  getContractNatureLabel,
  getProcedureLabel,
} from "../../sourcing-events/portal/utils/displayFormatters";
import { LabelValueGrid } from "../../sourcing-events/portal/view-single/LabelValueGrid";
import { SourcingEventV2StatusTag } from "./SourcingEventV2StatusTag";

const DASH = "–";

const AWARDABLE_STATUSES: SourcingEventStatus[] = [
  SourcingEventStatus.Evaluation,
  SourcingEventStatus.AwardDecision,
  SourcingEventStatus.Awarded,
  SourcingEventStatus.Closed,
];

interface Props {
  event: SourcingEventV2Dto;
  canEdit?: boolean;
  onEdit?: () => void;
  onViewPublic?: () => void;
}

export const EventHeader = ({ event, canEdit, onEdit, onViewPublic }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayDate = useDisplayDate();
  const canGenerateProtocol = AWARDABLE_STATUSES.includes(event.status);
  const { eventData, publicContext } = event;
  const value = publicContext.estimatedValue;
  const doffinReference = publicContext.publications?.find(
    (p) => p.channel === PublicationChannel.Doffin && p.externalId,
  )?.externalId;

  return (
    <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p={{ base: "4", md: "6" }}>
      <Breadcrumb separator={<Icon as={FiChevronRight} color="smMuted" boxSize="3" />} fontSize="sm" mb="3">
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to={urls.v2.sourcingEvents.viewAll} color="smPrimary">
            {t("All procurements")}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink color="smMuted" isTruncated maxW="320px">
            {eventData.title}
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <Flex direction={{ base: "column", lg: "row" }} justify="space-between" gap="4" mb="5">
        <HStack spacing="3" align="center" flexWrap="wrap">
          <Heading as="h1" size="lg" color="smText">
            {eventData.title || t("Untitled")}
          </Heading>
          <SourcingEventV2StatusTag status={event.status} />
        </HStack>
        <HStack spacing="2" flexShrink={0}>
          {canEdit && (
            <Button size="sm" variant="outline" leftIcon={<Icon as={FiEdit2} />} onClick={onEdit}>
              {t("Edit")}
            </Button>
          )}
          <Button size="sm" variant="outline" leftIcon={<Icon as={FiExternalLink} />} onClick={onViewPublic}>
            {t("View public version")}
          </Button>
          <Explanation text={t("Complete the evaluation first")} enabled={!canGenerateProtocol}>
            <Button
              size="sm"
              variant="outline"
              leftIcon={<Icon as={FiFileText} />}
              isDisabled={!canGenerateProtocol}
              onClick={() => navigate(urls.v2.sourcingEvents.protocol.url(event.id))}
            >
              {t("Generate protocol")}
            </Button>
          </Explanation>
        </HStack>
      </Flex>

      <LabelValueGrid
        rows={[
          {
            label: t("Deadline"),
            value: eventData.bidSubmissionDeadline ? displayDate(eventData.bidSubmissionDeadline) : DASH,
          },
          {
            label: t("Question deadline"),
            value: event.questionDeadline ? displayDate(event.questionDeadline) : DASH,
          },
          {
            label: t("Procedure"),
            value: publicContext.procedureType ? getProcedureLabel(publicContext.procedureType, t) : DASH,
          },
          {
            label: t("Contract type"),
            value: publicContext.contractNature ? getContractNatureLabel(publicContext.contractNature, t) : DASH,
          },
          {
            label: t("Estimated value"),
            value: value?.value != null ? formatEstimatedValue(value.value, value.currency) : DASH,
          },
          ...(doffinReference ? [{ label: t("Doffin reference"), value: doffinReference }] : []),
        ]}
      />
    </Box>
  );
};
